import _ from 'underscore';
import ko from 'knockout';

import { DatePicker } from '@bingads-webui/date-picker';
import { TimePicker } from '@bingads-webui/time-picker';
import { validateDateRange } from '@bingads-webui-universal/primitive-utilities';
import { helpers } from '@bingads-webui/deterministic-view';
import { KnockoutView } from '@bingads-webui/knockout-view';

import template from './custom-time-range-selection-view-template.pug';

const { ensureChildView } = helpers;


function joinDateAndTime(date, time) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
    time.getSeconds()
  );
}

function initializeChildView(view) {
  const { minDate, maxDate } = view.options;

  ensureChildView(view, 'datePicker', DatePicker, {
    minDate,
    maxDate,
  }, {
    dateChanged: (date) => {
      view.setState({ date });
    },
  });
  ensureChildView(view, 'startTimePicker', TimePicker, {}, {
    timeChanged: ({ time }) => {
      view.setState({ startTime: time });
    },
  });
  ensureChildView(view, 'endTimePicker', TimePicker, {}, {
    timeChanged: ({ time }) => {
      view.setState({ endTime: time });
    },
  });
}

function getDateRange(view) {
  const { date, startTime, endTime } = view.getState();
  const startDate = joinDateAndTime(date, startTime);
  const endDate = joinDateAndTime(date, endTime);

  return { startDate, endDate };
}

function validate(view) {
  const { minDate, maxDate, i18n } = view.options;
  const { startDate, endDate } = getDateRange(view);

  const generalErrors = (endDate <= startDate) ? [i18n.getString('StartTime_GreaterThanOrEqual_EndTime')] : [];
  const startDateErrors = _.map(
    validateDateRange(minDate, maxDate, startDate),
    key => i18n.getString(key)
  );
  const endDateErrors = _.map(
    validateDateRange(minDate, maxDate, endDate),
    key => i18n.getString(key)
  );

  view.viewModel.generalErrors(generalErrors);
  view.viewModel.startDateErrors(startDateErrors);
  view.viewModel.endDateErrors(endDateErrors);
}

class CustomTimeRangeSelectionPanelViewModel {
  constructor({
    generalErrors,
    startDateErrors,
    endDateErrors,
  } = {}) {
    this.generalErrors = ko.observable(generalErrors);
    this.startDateErrors = ko.observable(startDateErrors);
    this.endDateErrors = ko.observable(endDateErrors);
    this.errors = ko.computed(() => [].concat(
      this.generalErrors(),
      this.startDateErrors(),
      this.endDateErrors()
    ));
  }
}

/**
 * This panel allows selection of a custom time range
 * @param {Object} i18n - the i18n model
 * @param {Date} minDate - the minimum value of date
 * @param {Date} maxDate - the maximum value of date
 * @extends {View}
 */
export class CustomTimeRangeSelectionPanel extends KnockoutView {
  initialize({
    i18n,
    minDate,
    maxDate,
  }) {
    super.initialize();
    this.options = { i18n, minDate, maxDate };
    initializeChildView(this);
  }

  setState({
    date,
    startTime,
    endTime,
  }) {
    if (date) {
      this.getChild('datePicker').setState({ selectedDate: date });
    }
    if (startTime) {
      this.getChild('startTimePicker').setState({ time: startTime });
    }
    if (endTime) {
      this.getChild('endTimePicker').setState({ time: endTime });
    }
    validate(this);
    return this;
  }

  getState() {
    const date = this.getChild('datePicker').getState().selectedDate;
    const startTime = this.getChild('startTimePicker').getState().time;
    const endTime = this.getChild('endTimePicker').getState().time;

    return { date, startTime, endTime };
  }


  getSelection() {
    const errors = this.viewModel.errors();
    const { startDate, endDate } = getDateRange(this);

    return {
      start: startDate,
      end: endDate,
      invalid: errors.length > 0,
    };
  }

  get template() {
    return template;
  }

  get ViewModel() {
    return CustomTimeRangeSelectionPanelViewModel;
  }
}
